import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { CalendarIcon } from '@heroicons/react/24/outline';

const DatePickerInput = React.forwardRef(({ value, onClick }, ref) => (
  <div
    onClick={onClick}
    ref={ref}
    className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
      text-gray-900 dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600
      focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent flex items-center"
  >
    <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" aria-hidden="true" />
    <span className="flex-1 text-left">{value || 'Select date'}</span>
  </div>
));

export default function AddExpenseModal({ isOpen, onClose, vehicles, onSubmit }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date(),
    vehicleId: '',
    category: 'fuel',
    amount: '',
    notes: '',
  });

  const expenseCategories = [
    { id: 'fuel', name: 'Fuel Costs' },
    { id: 'food', name: 'Food Expenses' },
    { id: 'wages', name: 'Driver Wages' },
    { id: 'misc', name: 'Miscellaneous Costs' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vehicleId) {
      alert('Please select a vehicle');
      return;
    }
    setLoading(true);
    try {
      const vehicle = vehicles.find(v => v.id === formData.vehicleId);
      await onSubmit({ 
        ...formData,
        date: formData.date.toISOString().split('T')[0],
        vehicleRegistration: vehicle.registrationNumber,
        driverName: vehicle.driverName
      });
      
      // Reset form data after successful submission
      setFormData({
        date: new Date(),
        vehicleId: '',
        category: 'fuel',
        amount: '',
        notes: '',
      });
      
      onClose();
    } catch (error) {
      console.error('Error adding expense:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full sm:max-w-md transform overflow-visible bg-white dark:bg-gray-800 
                p-4 sm:p-6 text-left align-middle shadow-xl transition-all rounded-t-2xl sm:rounded-2xl">
                <div className="flex items-center justify-between mb-5">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    Add New Expense
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500 sm:hidden"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Date Picker */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Date
                      </label>
                      <DatePicker
                        selected={formData.date}
                        onChange={(date) => handleChange('date', date)}
                        customInput={<DatePickerInput />}
                        dateFormat="dd/MM/yyyy"
                      />
                    </div>

                    {/* Amount Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Amount
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <span className="text-gray-500 dark:text-gray-400">₹</span>
                        </div>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={formData.amount}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              handleChange('amount', value);
                            }
                          }}
                          className="w-full px-4 pl-8 py-2.5 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                            text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400
                            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="0.00"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Vehicle Select */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Vehicle
                    </label>
                    <select
                      value={formData.vehicleId}
                      onChange={(e) => handleChange('vehicleId', e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                        text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400
                        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select Vehicle</option>
                      {vehicles.map((vehicle) => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.registrationNumber} - {vehicle.driverName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Category Select */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Category
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => handleChange('category', e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                        text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400
                        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      {expenseCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Notes Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                        text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400
                        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Optional notes"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                    <button
                      type="button"
                      onClick={onClose}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
                        bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg
                        hover:bg-gray-50 dark:hover:bg-gray-600 
                        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white 
                        bg-primary-600 rounded-lg border border-transparent
                        hover:bg-primary-700 
                        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent 
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Adding...' : 'Add Expense'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}